import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, HeartPulse, Clock } from "lucide-react";
import { FaHandHoldingHeart, FaTeeth, FaTeethOpen, FaTooth, FaChildReaching } from "react-icons/fa6";
import { GiScrewdriver } from "react-icons/gi";
import { CLINIC } from "@/data/clinic";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import SeoImage from "@/components/SeoImage";
import { dentistJsonLd, websiteJsonLd } from "@/lib/schema";
import Testimonials from "@/components/clinic/Testimonials";
import { useI18n } from "@/lib/i18n";

// Dental-specific icons mapped by treatment.icon string returned by backend
const TREATMENT_ICONS = {
  "heart-pulse": FaHandHoldingHeart,       // Odontología sin dolor
  "sparkles": FaTeethOpen,                 // Estética dental (sonrisa)
  "align-justify": FaTeeth,                // Ortodoncia (dientes alineados)
  "anchor": GiScrewdriver,                 // Implantes
  "stethoscope": FaTooth,                  // Odontología general
  "baby": FaChildReaching,                 // Odontopediatría
};

export default function Home() {
  const { t, pick } = useI18n();
  const [treatments, setTreatments] = useState([]);
  const [content, setContent] = useState(null);
  useEffect(() => {
    api.get("/treatments").then((r) => setTreatments(r.data)).catch(() => {});
    api.get("/site-content").then((r) => setContent(r.data)).catch(() => setContent({}));
  }, []);
  const c = content || {};
  const hero = c.home_hero || {};
  const trust = c.home_trust || {};
  const tIntro = c.home_treatments_intro || {};
  const conf = c.home_confidence || {};
  const finalCta = c.home_final_cta || {};

  return (
    <div data-testid="home-page">
      <SEOHead
        title="Odontología sin dolor en Armenia, Quindío · Katia Falón Odontología"
        description="Odontología sin dolor y odontopediatría en Armenia, Quindío. Atención personalizada con anestesia electrónica, planes de tratamiento claros y ambiente amable para adultos y niños. Agenda tu valoración."
        path="/"
        image="/logo-katia.png"
        jsonLd={[dentistJsonLd(), websiteJsonLd()]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:px-10 lg:py-24">
          <div className="flex flex-col justify-center">
            <div
              data-testid="hero-badge-sin-dolor"
              className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-[hsl(var(--clinic-muted))]"
            >
              <Sparkles size={14} className="shrink-0" />
              <span className="leading-none">{pick(hero.badge, "hero.badge")}</span>
            </div>
            <h1 data-testid="hero-title" className="font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
              {pick(hero.title1, "hero.title1")} <em className="italic text-[hsl(var(--clinic-secondary))]">{pick(hero.title2, "hero.title2")}</em>{pick(hero.title3, "hero.title3")}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[hsl(var(--clinic-muted))]">
              {pick(hero.subtitle, "hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to={hero.cta_primary_link || "/reservar"}
                data-testid="hero-book-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--clinic-secondary))] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
              >
                {pick(hero.cta_primary_label, "heroCta.primary")} <ArrowRight size={16} />
              </Link>
              <a
                href={hero.cta_secondary_link || `tel:${CLINIC.phone}`}
                data-testid="hero-phone-cta"
                className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--clinic-text))] px-6 py-3 text-sm font-semibold hover:bg-[hsl(var(--clinic-text))] hover:text-white"
              >
                {hero.cta_secondary_label || `${t("heroCta.secondary")} ${CLINIC.phoneDisplay}`}
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-[hsl(var(--clinic-border))] pt-8">
              {[
                { icon: HeartPulse,   label: pick(trust.card1_title, "hero.trust1_title"), note: pick(trust.card1_note, "hero.trust1_note") },
                { icon: ShieldCheck,  label: pick(trust.card2_title, "hero.trust2_title"), note: pick(trust.card2_note, "hero.trust2_note") },
                { icon: Clock,        label: pick(trust.card3_title, "hero.trust3_title"), note: pick(trust.card3_note, "hero.trust3_note") },
              ].map((s) => (
                <div key={s.label} className="flex flex-col items-center text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--clinic-primary))]/10 text-[hsl(var(--clinic-primary))]">
                    <s.icon size={30} strokeWidth={1.8} />
                  </div>
                  <div className="mt-3 text-base font-bold tabular-nums lining-nums">{s.label}</div>
                  <div className="mt-0.5 text-xs text-[hsl(var(--clinic-muted))]">{s.note}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 -z-10 translate-x-4 translate-y-4 rounded-3xl bg-[hsl(var(--clinic-primary))]/20" />
            {hero.image_url ? (
              <img
                src={hero.image_url}
                alt="Consultorio Katia Falón Odontología"
                className="aspect-square h-auto w-full rounded-3xl object-cover shadow-xl"
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
            ) : (
              <SeoImage
                slug="dra-katia-falon-consultorio-dental-armenia"
                alt="Dra. Katia Falón ajustando el equipo dental en su consultorio de Armenia, Quindío — odontología sin dolor"
                caption="Consultorio de Katia Falón Odontología en Armenia, Quindío"
                schema
                eager
                className="aspect-square h-auto w-full rounded-3xl object-cover shadow-xl"
                testId="hero-image"
              />
            )}
          </div>
        </div>
      </section>

      {/* Treatments */}
      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
        <div className="mb-10 flex items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">{t("home.treatmentsEyebrow")}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl">{t("home.treatmentsTitle")}</h2>
          </div>
          <Link to="/tratamientos" className="hidden text-sm font-semibold text-[hsl(var(--clinic-secondary))] lg:inline">
            {t("home.treatmentsViewAll")}
          </Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {treatments.map((tr, i) => {
            const Icon = TREATMENT_ICONS[tr.icon] || FaTooth;
            const isFeatured = i === 0;
            return (
              <Link
                key={tr.slug}
                to={`/tratamientos/${tr.slug}`}
                data-testid={`home-treatment-${tr.slug}`}
                className={`group relative flex flex-col justify-between rounded-2xl border border-[hsl(var(--clinic-border))] bg-white p-6 transition-transform hover:-translate-y-1 hover:shadow-lg ${
                  isFeatured ? "sm:col-span-2 lg:col-span-2 lg:row-span-2 bg-gradient-to-br from-white to-[hsl(var(--clinic-bg))] p-8" : ""
                }`}
              >
                <div>
                  <div className="flex items-start justify-between">
                    <div className="text-xs uppercase tracking-widest text-[hsl(var(--clinic-muted))]">0{i + 1}</div>
                    <div className={`flex ${isFeatured ? "h-24 w-24" : "h-16 w-16"} items-center justify-center rounded-2xl bg-[hsl(var(--clinic-primary))]/10 text-[hsl(var(--clinic-secondary))] transition-colors group-hover:bg-[hsl(var(--clinic-secondary))] group-hover:text-white`} aria-hidden>
                      <Icon size={isFeatured ? 56 : 36} />
                    </div>
                  </div>
                  <h3 className={`mt-5 font-display ${isFeatured ? "text-3xl lg:text-4xl" : "text-2xl"}`}>{tr.name}</h3>
                  <p className="mt-3 text-sm text-[hsl(var(--clinic-muted))]">{tr.tagline}</p>
                </div>
                <div className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--clinic-secondary))]">
                  {t("common.viewDetail")} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Trust — E-E-A-T lite */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2 lg:px-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">{pick(conf.eyebrow, "home.confidenceEyebrow")}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl">{pick(conf.title, "home.confidenceTitle")}</h2>
            <p className="mt-6 text-base leading-relaxed text-[hsl(var(--clinic-muted))]">
              {pick(conf.body, "home.confidenceBody")}
            </p>
            <p className="mt-4 rounded-xl bg-[hsl(var(--clinic-bg))] p-4 text-sm italic text-[hsl(var(--clinic-muted))]">
              {pick(conf.note, "home.confidenceNote")}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { k: pick(conf.card1_k, "home.confCard1_k"), v: pick(conf.card1_v, "home.confCard1_v") },
              { k: pick(conf.card2_k, "home.confCard2_k"), v: pick(conf.card2_v, "home.confCard2_v") },
              { k: pick(conf.card3_k, "home.confCard3_k"), v: pick(conf.card3_v, "home.confCard3_v") },
              { k: pick(conf.card4_k, "home.confCard4_k"), v: pick(conf.card4_v, "home.confCard4_v") },
            ].map((c) => (
              <div key={c.k} className="rounded-2xl border border-[hsl(var(--clinic-border))] p-6">
                <div className="font-display text-3xl text-[hsl(var(--clinic-secondary))]">{c.k}</div>
                <div className="mt-2 text-sm text-[hsl(var(--clinic-muted))]">{c.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials (only rendered if there are consented published ones) */}
      <Testimonials />

      {/* Final CTA */}
      <section className="mx-auto my-16 max-w-7xl px-6 lg:my-24 lg:px-10">
        <div className="rounded-3xl bg-[hsl(var(--clinic-text))] p-10 text-white lg:p-16">
          <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr] lg:items-end">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">{pick(finalCta.title, "home.finalCtaTitle")}</h2>
              <p className="mt-4 max-w-xl text-white/70">
                {pick(finalCta.body, "home.finalCtaBody")}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/reservar"
                data-testid="cta-book-bottom"
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[hsl(var(--clinic-text))]"
              >
                {pick(finalCta.book_label, "home.finalCtaBook")} <ArrowRight size={16} />
              </Link>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-semibold"
              >
                {pick(finalCta.doubt_label, "home.finalCtaDoubt")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
